// Buyer advertising (WS6) — the wire mirror of AdvertisingController
// (api/buyer-advertising).
//
// 🔴 Every figure here is MANAGEMENT REPORTING. Nothing on this surface touches
// income, the waterfall, a disbursement, a commission or a 1099. Advertising
// money leaves through somebody else's rails and is written down so it can be
// measured against what it produced.
//
// Reads gate on BuyerAdvertising.Read and are narrowed further to the caller's
// team; advertisement writes and spend entry on BuyerAdvertising.ManageAds;
// budgets on BuyerAdvertising.ManageBudget; the cross-team roll-up on
// Buyers.ManageAllTeams.

import { api } from "./client";

/**
 * The C# constant is string-stored, so these are the literal wire values. A
 * channel added server-side must be added here too or it renders as a raw
 * token — and, more importantly, disappears from the picker.
 */
export type AdvertisingChannel =
  | "BizBuySell"
  | "BizQuest"
  | "LoopNet"
  | "BusinessesForSale"
  | "GoogleSearch"
  | "Meta"
  | "YouTube"
  | "LinkedIn"
  | "Email"
  | "DirectMail"
  | "Event"
  | "Other";

export type AdvertisementStatus = "Draft" | "Running" | "Paused" | "Archived";

export interface AdvertisingTeamOptionDto {
  id: string;
  name: string;
  /** The WRITE scope, resolved server-side. The page renders actions from this
   *  rather than deriving them, so it cannot offer what the endpoint refuses. */
  canManage: boolean;
}

/** A prequalification site an advertisement may point at — attribution layer 1.
 *  Carried on the scope payload because `Partners.ManageBrands` does not reach
 *  F1, the role that configures advertisements. */
export interface AdvertisingBrandOptionDto {
  id: string;
  displayName: string;
}

export interface AdvertisingScopeDto {
  teams: AdvertisingTeamOptionDto[];
  brands: AdvertisingBrandOptionDto[];
  defaultTeamId?: string | null;
  canManageAllTeams: boolean;
  canManageAds: boolean;
  canManageBudget: boolean;
}

export interface AdvertisingBudgetDto {
  id: string;
  buyerTeamId: string;
  /** `YYYY-MM`. */
  period: string;
  /** `YYYY-MM-DD` — a wall-calendar date. Render with `formatCalendarDate`,
   *  NEVER with a timezone-aware formatter. */
  periodStart: string;
  periodEnd: string;
  amount: number;
  setByUserId?: string | null;
  setByDisplayName?: string | null;
  notes?: string | null;
  modifiedAt?: string | null;
}

export interface SaveAdvertisingBudgetPayload {
  buyerTeamId: string;
  /** Any day in the target month — the server normalises to the 1st and
   *  computes the end itself. */
  periodStart: string;
  amount: number;
  notes?: string | null;
}

export interface AdvertisementMetricsDto {
  spendToDate: number;
  plannedBudget?: number | null;
  /** A FRACTION (0.42 is 42%). Named for the unit because a bare "utilization"
   *  is the kind of number one tier multiplies by a hundred and the other does
   *  not. */
  budgetUtilizationFraction?: number | null;
  leads: number;
  qualified: number;
  converted: number;
  /** 🔴 Null when the denominator is zero — never 0. An advertisement with no
   *  leads has NO cost per lead, and `$0.00` says the opposite of the truth
   *  about the worst row on the page. Render an em dash. */
  costPerLead?: number | null;
  costPerQualified?: number | null;
  costPerDeal?: number | null;
}

export interface AdvertisementDto {
  id: string;
  buyerTeamId: string;
  buyerTeamName?: string | null;
  name: string;
  channel: AdvertisingChannel;
  status: AdvertisementStatus;
  partnerBrandId?: string | null;
  partnerBrandName?: string | null;
  startDate: string;
  endDate?: string | null;
  notes?: string | null;
  /** Currently in a status that may claim an inbound lead. Rendered, because a
   *  Draft producing no leads is a setting rather than a performance, and the
   *  two look identical in a metrics column. */
  claimsLeads: boolean;
  /** No branded site is set, so nothing can EVER be
   *  attributed. Say so — a permanent zero reads as "this produced nothing". */
  hasNoAttributionSignal: boolean;
  metrics: AdvertisementMetricsDto;
  createdAt: string;
  /** The copy a manager wrote for the placement — what the visitor read. */
  adCopy?: string | null;
  industry?: string | null;
  location?: string | null;
  /** USPS two-letter code. */
  locationState?: string | null;
  targetPriceMin?: number | null;
  targetPriceMax?: number | null;
  /** Immutable after create. The per-ad attribution credential that rides
   *  `?ad=` on the team's landing page. Always on the wire (NOT NULL
   *  server-side); optional here only so the intake pickers' fixtures, which
   *  never read it, need not carry one. */
  linkToken?: string;
  /** `/buy/{teamSiteSlug}?ad={linkToken}`, or null when the team has no site
   *  — there is then no page for the link to point at, and the surface says
   *  so rather than composing a URL to nowhere. */
  linkPath?: string | null;
}

export interface CreateAdvertisementPayload {
  buyerTeamId: string;
  name: string;
  channel: AdvertisingChannel;
  status?: AdvertisementStatus | null;
  partnerBrandId?: string | null;
  plannedBudget?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
  adCopy?: string | null;
  industry?: string | null;
  location?: string | null;
  locationState?: string | null;
  targetPriceMin?: number | null;
  targetPriceMax?: number | null;
}

/** FIND-12: omitted/null = unchanged, "" clears a nullable string, and the
 *  `clear*` flags clear the nullable value types. The owning team is
 *  deliberately absent — moving an advertisement between teams would move its
 *  spend history and every lead it has been credited with. */
export interface UpdateAdvertisementPayload {
  name?: string | null;
  channel?: AdvertisingChannel | null;
  status?: AdvertisementStatus | null;
  partnerBrandId?: string | null;
  clearPartnerBrand?: boolean;
  plannedBudget?: number | null;
  clearPlannedBudget?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  clearEndDate?: boolean;
  notes?: string | null;
  adCopy?: string | null;
  industry?: string | null;
  location?: string | null;
  locationState?: string | null;
  targetPriceMin?: number | null;
  targetPriceMax?: number | null;
  /** Both bounds blank on the form = the range is cleared, as one flag. */
  clearTargetPriceRange?: boolean;
}

export interface AdvertisementSpendEntryDto {
  id: string;
  advertisementId: string;
  spendDate: string;
  amount: number;
  recordedByUserId?: string | null;
  recordedByDisplayName?: string | null;
  notes?: string | null;
  isAdjustment: boolean;
  adjustsSpendEntryId?: string | null;
  createdAt: string;
}

export interface AdvertisementSpendLedgerDto {
  advertisementId: string;
  /** The SUM of `entries`, adjustments included — so a reader can check the
   *  headline against the rows rather than taking it on trust. */
  total: number;
  entries: AdvertisementSpendEntryDto[];
}

export interface RecordAdvertisementSpendPayload {
  spendDate: string;
  amount: number;
  notes?: string | null;
  sourceKey?: string | null;
}

export interface AdjustAdvertisementSpendPayload {
  adjustsSpendEntryId: string;
  /** The delta. Negative takes money back out. */
  amount: number;
  spendDate?: string | null;
  reason: string;
  sourceKey?: string | null;
}

export interface AdvertisingTeamRollupRowDto {
  buyerTeamId: string;
  teamName: string;
  budget?: number | null;
  spendToDate: number;
  utilizationFraction?: number | null;
  /** A FLAG, never a block — the spend already happened. */
  isOverBudget: boolean;
  leads: number;
  qualified: number;
  costPerLead?: number | null;
}

export interface AdvertisingRollupDto {
  period: string;
  periodStart: string;
  periodEnd: string;
  totalBudget: number;
  totalSpend: number;
  totalLeads: number;
  blendedCostPerLead?: number | null;
  teams: AdvertisingTeamRollupRowDto[];
}

/** One advertisement in one month, from the cost-per-lead report. */
export interface AdvertisingCostPerLeadRowDto {
  advertisementId: string;
  advertisementName: string;
  channel: AdvertisingChannel;
  buyerTeamId: string;
  buyerTeamName: string;
  /** `YYYY-MM`. */
  period: string;
  spend: number;
  leads: number;
  qualified: number;
  /** Null when the denominator is zero — never 0. */
  costPerLead?: number | null;
  costPerQualified?: number | null;
}

/** One team's budget against its spend for one month. */
export interface AdvertisingBudgetPaceRowDto {
  buyerTeamId: string;
  buyerTeamName: string;
  /** `YYYY-MM`. */
  period: string;
  /** Null when no budget was set for the month — distinct from a zero budget. */
  budget?: number | null;
  spend: number;
  /** A FLAG, never a block — the spend already happened. */
  isOverBudget: boolean;
}

export interface AdvertisingCostPerLeadDto {
  rows: AdvertisingCostPerLeadRowDto[];
  budgets: AdvertisingBudgetPaceRowDto[];
}

export interface AdvertisingCostPerLeadQuery {
  /** `YYYY-MM`, inclusive. */
  from: string;
  /** `YYYY-MM`, inclusive; at most twelve months after `from`. */
  to: string;
  /** Narrowed server-side regardless: a one-team caller sees only their team. */
  buyerTeamId?: string | null;
}

const BASE = "/buyer-advertising";

export const advertisingApi = {
  scope: () => api.get<AdvertisingScopeDto>(`${BASE}/scope`),

  budgets: (teamId: string) =>
    api.get<AdvertisingBudgetDto[]>(`${BASE}/teams/${teamId}/budgets`),

  saveBudget: (payload: SaveAdvertisingBudgetPayload) =>
    api.put<AdvertisingBudgetDto>(`${BASE}/budgets`, payload),

  advertisements: (teamId: string) =>
    api.get<AdvertisementDto[]>(`${BASE}/teams/${teamId}/advertisements`),

  advertisement: (id: string) => api.get<AdvertisementDto>(`${BASE}/advertisements/${id}`),

  /** Every non-archived advertisement across the teams the caller READS —
   *  what the intake "Advertisement" pickers offer. Scoped server-side by
   *  `AdvertisingTeamScope`, so a one-team manager sees their team's and an
   *  all-teams holder sees everything; an F2, who holds no advertising
   *  permission, gets a 403 and the pickers stay hidden. */
  attributable: () => api.get<AdvertisementDto[]>(`${BASE}/advertisements/attributable`),

  createAdvertisement: (payload: CreateAdvertisementPayload) =>
    api.post<AdvertisementDto>(`${BASE}/advertisements`, payload),

  updateAdvertisement: (id: string, payload: UpdateAdvertisementPayload) =>
    api.patch<AdvertisementDto>(`${BASE}/advertisements/${id}`, payload),

  spend: (advertisementId: string) =>
    api.get<AdvertisementSpendLedgerDto>(`${BASE}/advertisements/${advertisementId}/spend`),

  recordSpend: (advertisementId: string, payload: RecordAdvertisementSpendPayload) =>
    api.post<AdvertisementSpendLedgerDto>(
      `${BASE}/advertisements/${advertisementId}/spend`, payload),

  adjustSpend: (advertisementId: string, payload: AdjustAdvertisementSpendPayload) =>
    api.post<AdvertisementSpendLedgerDto>(
      `${BASE}/advertisements/${advertisementId}/spend/adjustments`, payload),

  rollup: (period: string) => api.get<AdvertisingRollupDto>(`${BASE}/rollup`, { period }),

  /** Month-bucketed spend, leads and budget pace. Gated `BuyerAdvertising.Read`
   *  and narrowed to the caller's team in-service, so unlike `rollup` a
   *  one-team manager may call it — which is what gives the team tab a
   *  month-to-date figure at all. */
  costPerLead: (query: AdvertisingCostPerLeadQuery) =>
    api.get<AdvertisingCostPerLeadDto>(`${BASE}/cost-per-lead`, {
      from: query.from,
      to: query.to,
      ...(query.buyerTeamId ? { buyerTeamId: query.buyerTeamId } : {}),
    }),
};
